import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCServicesApi, RCCTextArea } from "react-common-components";
import { useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import ModalSide from "src/components/Modal/styles";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { useHistory } from "react-router-dom";
import { IDashboard, IRefDashboardModals } from "./interface";
import addAndChangeSchema from "./schemas";

const ModalsDashboard: React.ForwardRefRenderFunction<IRefDashboardModals> = (_, ref) => {
	const { translate } = useTranslation();
	const {addToast} = RCCHookToast();
	const { confirm } = useConfirmModal();
	const history = useHistory();
	const onCallbackFunction = useRef<Function | undefined>(undefined);

	const [selectedDashboard, setSelectedDashboard] = useState<IDashboard>({} as IDashboard);
    
	const actionButtonDelete = (id: number, onCallback?: Function) => {
		async function deleteMethod(){
			await RCCServicesApi.deleteRequest({
				method: "Dashboard/Delete",
				urlParameters: {
					id
				}
			});
		}
    
		confirm(
			translate("Exclusão de dashboard"), 
			`${translate("Deseja realmente excluir a dashboard")}?`,
			async () => {
				await deleteMethod();
				addToast({
					title: translate("Sucesso"),
					description: translate("Dashboard excluída com sucesso"),
					color: "green"
				});
				if(onCallback)
					onCallback(id);
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir dashboard"), addToast);
			},
			true
		);
	};

	
	// Modal Add/Change
	const modalAddOrChange = useRef<RCCRefModalProps>(null);
	const { handleSubmit : handleSubmitAddOrChange, register : registerAddOrChange, reset : resetAddOrChange, setError : setErrorAddOrChange, formState: { errors : errorsAddOrChange }, clearErrors: clearErrorsAddOrChange } = useForm();
	const submitAddOrChange = async (data: any) => {
		try{
			clearErrorsAddOrChange();
			modalAddOrChange.current?.setLoadingButton();
			
			await addAndChangeSchema(translate).validate({...data}, {
				abortEarly: false
			});

			const response = await RCCServicesApi.postRequest({
				method: "Dashboard/Insert",
				body: {
					...data
				}
			});
			const datba001 = response.data;

			addToast({
				color: "green",
				description: translate("Dashboard adicionada com sucesso"),
				title: translate("Sucesso")
			});

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();
            
			if(onCallbackFunction.current)
				onCallbackFunction.current(datba001);

			history.push(`/dashboard/${datba001.daa001Id}`);

		}catch(err){

			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddOrChange))
				return;

			standardErrorMessages(err, translate("Erro ao adicionar dashboard"), addToast);
		}
	};
	const actionButtonAdd = (onCallback?: Function) => {
		resetAddOrChange(); 
		setSelectedDashboard({} as IDashboard); 
		onCallbackFunction.current = onCallback;
		modalAddOrChange.current?.openModal();
	};
	
	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonDelete
	}));
    
	return (
		<>	
			{/* Modal ADD/CHANGE */}
			<Modal
				ref={modalAddOrChange}
				header={{
					title: translate("Adicionar dashboard"),
					icon: {
						name: "dashboard"
					}
				}}
				onSubmit={handleSubmitAddOrChange(submitAddOrChange)}
				styleInline={`${ModalSide} height: 400px; min-height: 400px;`}
			>
				<RCCGrid
					container
					item
					direction="column"
					alignItems="center"
				>
					<RCCGrid
						container 
						item
						xs={12}
						sm={10}
					>
						<RCCInput 
							register={registerAddOrChange}
							label={translate("Título")}
							name="daa001Titulo"
							required
							error={errorsAddOrChange.daa001Titulo}
							defaultValue={selectedDashboard.daa001Titulo}
							maxLength={100}
						/>
					</RCCGrid>
					<RCCGrid
						container 
						item
						xs={12}
						sm={10}
					>
						<RCCTextArea 
							register={registerAddOrChange}
							label={translate("Descrição")}
							name="daa001Descricao"
							error={errorsAddOrChange.daa001Descricao}
							defaultValue={selectedDashboard.daa001Descricao}
							maxLength={4000}
						/>
					</RCCGrid>
				</RCCGrid>
			</Modal>
		</>
	);
};

export default forwardRef(ModalsDashboard);
